import React, { Component } from 'react';
import { Draggable, Droppable } from 'react-beautiful-dnd';
import { connect } from 'react-redux';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import NodeHeader from '~/components/common/node-header';
import NewEntity from '~/components/common/new-entity';
import NewMatrixAnswerEntity from '~/components/quizzes/new-matrix-answer-entity';
import QuestionChildWorkspace from '~/components/quizzes/question-child-workspace';
import QuestionSettings from '~/components/quizzes/question-settings';
import QuestionContent from '~/components/quizzes/question-content';
import {
	setActiveLesson,
	toggleExpandEntity,
} from '~/redux/actions/workspaceActions';
import Icon from '~/components/common/icon';

/**
 * Question component for the workspace.
 * This is the parent component for all question types
 */
class QuestionWorkspace extends Component {
	/**
	 * Constructor
	 *
	 * @param {Object} props
	 */
	constructor( props ) {
		super( props );

		/**
		 * Our current state which contains the following
		 * - ID: current question ID
		 * - expanded: whether that node is expanded
		 * - isDropDisabled: whether the Droppable is enabled or not
		 * - workspace: our current workspace
		 * - locked: is editing locked? when editing an answer, the editing should be locked to avoid overriding unsaved content
		 * - editQuestionForm: is the question content form shown or not
		 * - showSettings: is the question settings form shown or not
		 * - showForm: is an answer form shown or not
		 */
		this.state = {
			ID: this.props.question.ID,
			expanded:
				-1 !==
				this.props.workspace.present.expandedItems.indexOf(
					this.props.question.ID
				),
			isDropDisabled: this.props.question.isLessonDropDisabled,
			workspace: this.props.workspace.present,
			locked: false,
			editQuestionForm: false,
			showSettings: false,
			showForm: false,
		};

		this.lockQuestion = this.lockQuestion.bind( this );
		this.unlockQuestion = this.unlockQuestion.bind( this );
		this.handleSettingsForm = this.handleSettingsForm.bind( this );
		this.handleShowForm = this.handleShowForm.bind( this );
		this.handleQuestionForm = this.handleQuestionForm.bind( this );
	}

	/**
	 * Check the current draggable type and match it against an answer.
	 *
	 * @param {Object} props
	 * @param {Object} state
	 */
	static getDerivedStateFromProps( props, state ) {
		const workspace = props.workspace.present;
		const prevWorkspace = state.workspace;
		const stateObject = {
			workspace,
		};

		// Listen to changes to the expanded state
		if ( workspace.expandedItems !== prevWorkspace.expandedItems ) {
			stateObject.expanded =
				-1 !== workspace.expandedItems.indexOf( state.ID );
			// If we're collapsing the question, unlock the question (remove the overlay)
			if ( ! stateObject.expanded ) {
				stateObject.locked = false;
			} else {
				// If we're expanding the question and the user was editing a form, lock the question (adds the overlay)
				if ( true === state.showSettings ) {
					stateObject.locked = true;
				}
				if ( true === state.showForm ) {
					stateObject.locked = true;
				}
				if ( true === state.editQuestionForm ) {
					stateObject.locked = true;
				}
			}
		}

		// Check for valid draggable types. It should be only answer.
		if (
			workspace.currentDraggableEntity !== state.currentDraggableEntity &&
			workspace.currentDraggableEntity
		) {
			let isDropDisabled = true;

			// Only Draggable answers can be dropped on that Droppable.
			if ( 'answer' === workspace.currentDraggableEntity.type ) {
				isDropDisabled = false;
			}

			stateObject.isDropDisabled = isDropDisabled;
		}

		return stateObject;
	}

	/**
	 * Locks the question to prevent editing
	 */
	lockQuestion() {
		this.setState( {
			locked: true,
		} );
	}

	/**
	 * Unlocks the question to allow editing
	 */
	unlockQuestion() {
		this.setState( {
			locked: false,
		} );
	}

	/**
	 * Toggles settings form
	 *
	 * @param {handleSettingsForm} e
	 */
	handleSettingsForm( e ) {
		if ( e ) {
			e.preventDefault();
		}

		if ( ! this.state.showSettings ) {
			this.lockQuestion();
		} else {
			this.unlockQuestion();
		}

		this.setState( ( { showSettings } ) => ( {
			showSettings: ! showSettings,
		} ) );
	}

	/**
	 * Displays edit mode
	 *
	 * @param {handleShowForm} e
	 */
	handleShowForm( e ) {
		if ( e ) {
			e.preventDefault();
		}

		if ( ! this.state.showForm ) {
			this.lockQuestion();
		} else {
			this.unlockQuestion();
		}

		this.setState( ( { showForm } ) => ( { showForm: ! showForm } ) );
	}

	/**
	 * Toggles question edit form
	 *
	 * @param {handleQuestionForm} e
	 */
	handleQuestionForm( e ) {
		if ( e ) {
			e.preventDefault();
		}

		if ( ! this.state.editQuestionForm ) {
			this.lockQuestion();
		} else {
			this.unlockQuestion();
		}

		this.setState( ( { editQuestionForm } ) => ( {
			editQuestionForm: ! editQuestionForm,
		} ) );
	}

	/**
	 * Render function
	 *
	 * @event handleSettingsForm
	 * @event handleShowForm
	 * @event handleQuestionForm
	 */
	render() {
		const {
			question,
			index,
			workspace,
			setActiveLesson,
			toggleExpandEntity,
		} = this.props;
		const {
			expanded,
			locked,
			showSettings,
			showForm,
			editQuestionForm,
		} = this.state;
		const answerTypeMap = LearnDashData.questions_types_map;

		// LEARNDASH-3940
		// Get answer data and retrieve contents of first answer.
		const answerData =
			question.answers &&
			question.answers[ answerTypeMap[ question.question_type ] ];
		const firstAnswer =
			answerData && answerData[ 0 ] && answerData[ 0 ].answer;

		return (
			// A lesson must be draggable for us to sort them
			<Draggable
				draggableId={ JSON.stringify( { ...question } ) }
				index={ index }
				key={ question.ID }
			>
				{ ( provided ) => (
					<div
						key={ question.ID }
						ref={ provided.innerRef }
						{ ...provided.draggableProps }
						className={ classNames( {
							'ld__builder--parent': true,
							'ld__builder--question': true,
							'-active':
								workspace.present.activeLesson === question.ID,
							'-expanded': expanded,
							'-locked': locked,
						} ) }
						onClick={ () =>
							setActiveLesson( {
								activeLesson: question.ID,
							} )
						}
					>
						<NodeHeader
							node={ question }
							provided={ provided }
							expanded={ expanded }
							className={ classNames( {
								'ld__builder-row-type': true,
								'-parent': true,
							} ) }
							toggleNode={ ( e ) => {
								e.preventDefault();
								toggleExpandEntity( index, question );
							} }
							type={ answerTypeMap[ question.question_type ] }
						/>
						{ question.edit_link && <div
							className={ classNames( {
								'ld__builder--child': true,
								'-question': true,
								'-expanded': expanded,
							} ) }
							key={ question.ID }
						>
							{ question && (
								<QuestionContent
									question={ question }
									editQuestionForm={ editQuestionForm }
									handleQuestionForm={
										this.handleQuestionForm
									}
									lockQuestion={ this.lockQuestion }
									unlockQuestion={ this.unlockQuestion }
								/>
							) }

							{ 'sort_answer' === question.question_type &&
								LearnDashData.labels.questions_types_description
									.sort_answer && (
									<p
										className="description"
										dangerouslySetInnerHTML={ {
											__html:
												LearnDashData.labels
													.questions_types_description
													.sort_answer,
										} }
									/>
								) }

							{ 'essay' !== question.question_type &&
								'' === firstAnswer && (
									<div className="ld__builder--edit-content-value">
										<div className="warning-icon -answer">
											<Icon icon="warning" />
										</div>
										&nbsp;
										<span className="warning description-red">
											{ `${ LearnDashData.i18n.answer_missing }` }
											.
										</span>
									</div>
								) }

							{ question.answers &&
								0 !== question.answers.length && (
									<Droppable
										droppableId={ JSON.stringify( {
											ID: question.ID,
											type: 'question',
											dnd: 'droppable-answer',
										} ) }
										isDropDisabled={
											this.state.isDropDisabled
										}
									>
										{ ( dropProvided, dropSnapshot ) => (
											<ul
												ref={ dropProvided.innerRef }
												className={ classNames( {
													'ld-question-answers-list': true,
													'-is-dragging-over':
														dropSnapshot.isDraggingOver,
												} ) }
											>
												{ Object.entries(
													question.answers
												).map( ( [ type, answer ] ) =>
													type ===
													answerTypeMap[
														question.question_type
													]
														? answer &&
														  0 !== answer.length &&
														  answer.map(
																(
																	answerItem,
																	index
																) => (
																	<QuestionChildWorkspace
																		key={ `answer-${ index }` }
																		node={
																			answerItem
																		}
																		question={
																			question
																		}
																		index={
																			index
																		}
																		showForm={
																			showForm
																		}
																		handleShowForm={
																			this
																				.handleShowForm
																		}
																		lockQuestion={
																			this
																				.lockQuestion
																		}
																		unlockQuestion={
																			this
																				.unlockQuestion
																		}
																	/>
																)
														  )
														: null
												) }
											</ul>
										) }
									</Droppable>
								) }

							{ ( 'single' === question.question_type ||
								'multiple' === question.question_type ||
								'sort_answer' === question.question_type ) && (
								<div className="ld__builder--new-entities -question">
									<NewEntity
										type="answer"
										parentLesson={ question.ID }
										question={ question }
									/>
								</div>
							) }
							{ 'matrix_sort_answer' ===
								question.question_type && (
								<div className="ld__builder--new-entities -question">
									<NewMatrixAnswerEntity
										type="answer"
										parentLesson={ question.ID }
										question={ question }
									/>
								</div>
								)
							}

							{
								question && 'essay' !== question.question_type &&
								<QuestionSettings
									question={ question }
									showSettings={ showSettings }
									handleSettingsForm={ this.handleSettingsForm }
									unlockQuestion={ this.unlockQuestion }
								/>
							}
						</div> }

					</div>
				) }
			</Draggable>
		);
	}
}

/**
 * Valid props
 */
QuestionWorkspace.propTypes = {
	questions: PropTypes.object,
	index: PropTypes.number,
	setActiveLesson: PropTypes.func,
	toggleExpandEntity: PropTypes.func,
	question: PropTypes.object,
	workspace: PropTypes.object,
};

/**
 *
 * @param {*} state
 */
const mapStateToProps = ( state ) => ( {
	...state,
} );

/**
 *
 * @param {*} dispatch
 */
const mapDispatchToProps = ( dispatch ) => ( {
	setActiveLesson: ( id ) => dispatch( setActiveLesson( id ) ),
	toggleExpandEntity: ( indexQuestion, node ) =>
		dispatch( toggleExpandEntity( indexQuestion, node ) ),
} );

export default connect(
	mapStateToProps,
	mapDispatchToProps
)( QuestionWorkspace );
